import { useState } from 'react';
import { EmptyState } from '@/components/EmptyState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { NewProjectDialog } from '@/features/projects/NewProjectForm';
import { ProjectsTable } from '@/features/projects/ProjectsTable';
import { useProjectActions } from '@/features/projects/useProjectActions';
import type { StepProps } from './StepShared';
import { StepCard } from './StepCard';
import { projectsNote } from './stepNotesModel';
import { STEP_TITLE } from './steps';
import type { Setup, SetupSearch } from './useSetup';

type ProjectActions = ReturnType<typeof useProjectActions>;

/** "New project" opens the project dialog. */
function NewProjectButton({ actions, current, onOpen }: { actions: ProjectActions; current: boolean; onOpen: () => void }) {
  return (
    <GatedButton size="sm" variant={current ? 'default' : 'outline'} data-testid="gs-new-project" can={actions.canCreate} aria-haspopup="dialog" onClick={onOpen}>
      <Icon name="add" size={16} />
      New project
    </GatedButton>
  );
}

interface BodyProps {
  setup: Setup;
  actions: ProjectActions;
  onSelect: (next: SetupSearch) => void;
}

/** The projects table, or the empty state. */
function ProjectsList({ setup, actions, onSelect }: BodyProps) {
  const projects = setup.projects.data ?? [];
  if (projects.length > 0) return <ProjectsTable projects={projects} appCounts={setup.appCounts} selectedId={setup.project?.id} onSelect={(p) => onSelect({ project: p.slug })} actionsFor={actions.actionsFor} />;
  return <EmptyState compact testId="gs-projects-empty" icon="account_tree" title="No project yet" description="Create the first one: a name and a slug. Staging, preprod and production come with it." />;
}

/** Step 2 · Projects: create one (dialog), pick the one this page is about, rename, archive. */
export function ProjectsStep({ setup, index, onSelect }: StepProps) {
  const actions = useProjectActions({ onArchived: (p) => (p.id === setup.project?.id ? onSelect({}) : undefined) });
  const [creating, setCreating] = useState(false);
  const current = setup.progress.next === 'project';
  const footer = <NewProjectButton actions={actions} current={current} onOpen={() => setCreating(true)} />;
  return (
    <>
      <StepCard
        state={setup.state('project')}
        current={current}
        index={index}
        title={STEP_TITLE.project}
        summary="A project groups the apps of one product with its environments: staging, preprod and production."
        collapsedNote={projectsNote(setup.project, (setup.projects.data ?? []).length)}
        footer={footer}
      >
        <ProjectsList setup={setup} actions={actions} onSelect={onSelect} />
      </StepCard>
      <NewProjectDialog open={creating} onOpenChange={setCreating} onCreated={(project) => onSelect({ project: project.slug })} />
      {actions.overlays}
    </>
  );
}
